import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EMPDETAILS } from "../../Redux/Slice/Partycalldetails";
import { useNavigate } from "react-router-dom";
import { FaMedal, FaStar } from "react-icons/fa";
import defaultProfile from "../../Image/Daynamic_Logo_Piture.png";
import { TiStarFullOutline } from "react-icons/ti";

const EmpProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState("All");
  const [availableRoles, setAvailableRoles] = useState([]);

  const { selectedPartyEMPDETAILS } = useSelector((state) => state.partyCall);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(EMPDETAILS());
      setTimeout(() => setLoading(false), 800);
    };
    fetchData();
  }, [dispatch]);

  // Extract unique roles from employee data
  useEffect(() => {
    if (selectedPartyEMPDETAILS?.data) {
      const rolePriority = {
        "Delphi Senior Developer": 1,
        "Web Senior Developer": 2,
        "Senior Customer Support": 3,
        "Delphi Junior Developer": 4,
        "Web Junior Developer": 5,
        "Junior Customer Support": 6,
      };

      const roles = [
        ...new Set(
          selectedPartyEMPDETAILS?.data
            ?.map((emp) => emp.Role)
            ?.filter(Boolean)
            ?.sort((a, b) => {
              return (rolePriority[a] || 999) - (rolePriority[b] || 999);
            })
        ),
      ];
      setAvailableRoles(["All", ...roles]);
    }
  }, [selectedPartyEMPDETAILS]);

  // Calculate average rating for an employee
  const calculateAverageRating = (ratingRecord) => {
    if (
      !ratingRecord ||
      !Array.isArray(ratingRecord) ||
      ratingRecord.length === 0
    ) {
      return 0;
    }

    // Filter out invalid ratings (null, undefined, empty strings, NaN)
    const validRatings = ratingRecord
      .map((r) => {
        const rating = parseFloat(r.Rating);
        return isNaN(rating) ? null : rating;
      })
      .filter((rating) => rating !== null && rating >= 0);

    if (validRatings.length === 0) return 0;

    const sum = validRatings.reduce((total, rating) => total + rating, 0);
    return sum / validRatings.length;
  };

  // Calculate top performers for each role
  const topPerformersByRole = useMemo(() => {
    if (!selectedPartyEMPDETAILS?.data) return {};

    const roleMap = {};
    const employees = selectedPartyEMPDETAILS.data;

    // Group employees by role
    employees.forEach((emp) => {
      if (!emp.Role) return;

      if (!roleMap[emp.Role]) {
        roleMap[emp.Role] = [];
      }
      roleMap[emp.Role].push(emp);
    });

    // Find top performer for each role
    const topPerformers = {};
    Object.keys(roleMap).forEach((role) => {
      const roleEmployees = roleMap[role];

      // Sort by completed tasks (descending), then by rating (descending)
      const sortedEmployees = roleEmployees.sort((a, b) => {
        const aCompleted = a.Completed || 0;
        const bCompleted = b.Completed || 0;

        // First priority: Completed tasks
        if (bCompleted !== aCompleted) {
          return bCompleted - aCompleted;
        }

        // Second priority: Average rating
        const aAvgRating = calculateAverageRating(a.RatingRecord);
        const bAvgRating = calculateAverageRating(b.RatingRecord);

        return bAvgRating - aAvgRating;
      });

      // Only mark as top performer if they have at least some completed tasks
      if (
        sortedEmployees.length > 0 &&
        (sortedEmployees[0].Completed || 0) > 0
      ) {
        topPerformers[sortedEmployees[0]._id] = role;
      }
    });

    return topPerformers;
  }, [selectedPartyEMPDETAILS]);

  const handleViewDetails = (empId, e) => {
    // Prevent event bubbling if needed
    if (e) {
      e.stopPropagation();
    }
    navigate(`/employee/${empId}`);
  };

  const handleCardClick = (empId) => {
    navigate(`/employee/${empId}`);
  };

  // Filter employees based on selected role
  const filteredEmployees =
    selectedPartyEMPDETAILS?.data?.filter(
      (emp) => selectedRole === "All" || emp.Role === selectedRole
    ) || [];

  // Skeleton loader component
  const SkeletonCard = () => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-pulse">
      <div className="relative h-[200px] bg-gray-300"></div>
      <div className="p-3 space-y-2">
        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-300 rounded"></div>
          ))}
        </div>
        <div className="h-9 bg-gray-300 rounded mt-2"></div>
      </div>
    </div>
  );

  // Star Rating Component
  const StarRating = ({ rating, maxRating = 10 }) => {
    const filledStars = Math.round(rating);

    return (
      <div className="flex justify-center mb-1">
        {[...Array(maxRating)].map((_, index) => (
          <span
            key={index}
            className={`text-lg mx-0.5 ${
              index < filledStars ? "text-yellow-500" : "text-gray-300"
            }`}
          >
            <TiStarFullOutline />
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {[...Array(10)].map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      {/* Role Filter Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">
          Filter by Role:
        </h2>
        <div className="flex flex-wrap gap-2">
          {availableRoles.map((role) => (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedRole === role
                  ? "All_button text-white shadow-md"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
            >
              {role}
            </button>
          ))}
        </div>

        {/* Selected filter info */}
        <div
          className="mt-3 text-sm "
          style={{
            color: "var(--All_button_bg_color)",
          }}
        >
          Showing {filteredEmployees.length}{" "}
          {selectedRole === "All"
            ? "employees"
            : `${selectedRole.toLowerCase()}s`}
        </div>
      </div>

      {/* Employee Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {filteredEmployees.map((emp, index) => {
          const isTopPerformer = topPerformersByRole[emp._id] === emp.Role;
          const averageRating = calculateAverageRating(emp.RatingRecord);
          const hasRatings =
            emp.RatingRecord &&
            emp.RatingRecord.length > 0 &&
            averageRating > 0;

          return (
            <div
              key={emp._id || index}
              className="group bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden 
                         transition-all duration-300 hover:-translate-y-1 hover:shadow-xl
                         relative cursor-pointer"
              onClick={() => handleCardClick(emp._id)}
            >
              {/* Top Performer Badge - Only show for the top performer in each role */}
              {isTopPerformer && (
                <div className="absolute top-2 right-2 z-20 animate-[fadeIn_0.3s_ease]">
                  <div
                    className="flex items-center gap-1 px-3 py-1 text-xs font-semibold text-white rounded-full shadow-lg All_button border-1"
                    style={{
                      border: "3px solid #FFD700",
                    }}
                  >
                    <FaMedal className="text-white text-sm drop-shadow" />
                    Top Performer {emp.Role}
                  </div>
                </div>
              )}

              {/* Image Section */}
              <div className="relative h-[300px] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10"></div>
                <img
                  src={emp.ProfilePhoto ? emp.ProfilePhoto : defaultProfile}
                  alt={emp.Name}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = defaultProfile;
                  }}
                />

                {/* Name and Role Overlay */}
                <div className="absolute bottom-3 left-3 z-20 text-white">
                  <h2 className="text-lg font-bold drop-shadow">{emp.Name}</h2>
                  <p className="text-xs opacity-95 font-medium bg-black/30 px-2 py-1 rounded-full inline-block mt-1">
                    {emp.Role}
                  </p>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-3">
                {/* Rating Section */}
                <div className="mb-2">
                  <div className="text-center bg-gray-50 rounded-lg p-2 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                      Avg Rating
                    </p>

                    {hasRatings ? (
                      <>
                        <StarRating rating={averageRating} />
                        <div className="flex items-center justify-center space-x-1 mt-1">
                          <span className="text-sm font-bold text-gray-800">
                            {averageRating.toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-500">/ 10</span>
                          <span className="text-xs text-gray-400 ml-1">
                            ({emp.RatingRecord?.length || 0} ratings)
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-center mb-1">
                          {[...Array(10)].map((_, index) => (
                            <span
                              key={index}
                              className="text-lg mx-0.5 text-gray-300"
                            >
                              <TiStarFullOutline />
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm font-medium text-gray-500">
                            No ratings yet
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Task Status */}
                <div className="p-2 text-center rounded-lg bg-blue-100 border border-gray-300 col-span-2 mb-2">
                  <p className="text-[10px] font-semibold text-gray-700 uppercase">
                    Task Status
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {(emp.Pending || 0) + (emp.Hold || 0) + (emp.Accept || 0)}
                  </p>
                </div>

                {/* Task Status Section */}
                <div className="mb-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 text-center rounded-lg bg-blue-50 border border-blue-200">
                      <p className="text-[10px] font-semibold text-blue-700 uppercase">
                        Running
                      </p>
                      <p className="text-base font-bold text-blue-800">
                        {emp.Accept || 0}
                      </p>
                    </div>
                    <div className="p-2 text-center rounded-lg bg-yellow-50 border border-yellow-200">
                      <p className="text-[10px] font-semibold text-yellow-700 uppercase">
                        In Queue
                      </p>
                      <p className="text-base font-bold text-yellow-800">
                        {emp.Pending || 0}
                      </p>
                    </div>

                    <div className="p-2 text-center rounded-lg bg-purple-50 border border-purple-200">
                      <p className="text-[10px] font-semibold text-purple-700 uppercase">
                        Hold
                      </p>
                      <p className="text-base font-bold text-purple-800">
                        {emp.Hold || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-2 text-center rounded-lg bg-green-100 border border-gray-300 col-span-2 mb-2">
                  <p className="text-[10px] font-semibold text-gray-700 uppercase">
                    Complete Tasks
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {emp.Completed || 0}
                  </p>
                </div>
                {/* Action Button */}
                <button
                  onClick={(e) => handleViewDetails(emp._id, e)}
                  className="w-full All_button text-white  
                           py-2 rounded-lg font-semibold transition-all duration-300 
                            hover:shadow
                           active:scale-95 transform focus:outline-none focus:ring-2 
                           focus:ring-indigo-500 focus:ring-offset-1 text-sm
                           relative z-30"
                >
                  View Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredEmployees.length === 0 && (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-600 mb-1">
            {selectedRole === "All"
              ? "No Team Members"
              : `No ${selectedRole}s Found`}
          </h3>
          <p className="text-gray-500 text-sm">
            {selectedRole === "All"
              ? "No team members available to display."
              : `No employees found with the role "${selectedRole}".`}
          </p>
          {selectedRole !== "All" && (
            <button
              onClick={() => setSelectedRole("All")}
              className="mt-3 px-4 py-2 All_button text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              Show All Employees
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmpProfile;
